import tw from 'twin.macro'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { orderBy } from 'lodash'
import { useFormContext } from 'react-hook-form'
import { Combobox } from '@headlessui/react'
import DatePicker from 'react-datepicker'

import 'react-datepicker/dist/react-datepicker.css'
import { CgImage, CgCalendar } from 'react-icons/cg'

import { useListenCoachOrgSlugQuery, useListenPublishedCheckoutPageQuery } from 'modules/PageBuilder/pageBuilderApi'
import {
  useListenItemPreviewImgQuery,
  useListenProductImgQuery,
  useSetOneTimeProductMutation,
  useSetStripeRequestMutation,
} from 'modules/ProductInfo/productInfoApi'
import { useSetExclusiveTabMutation, useSetTabContentMutation } from 'modules/Layout/layoutApi'

import { SELL } from 'modules/App/components/Header'
import { useFormRefsControl } from 'common/components/RefsControl/FormRefsControl/useFormRefsControl'
import { Input, inputBase } from 'common/components/Input/Input'
import { UploadInput } from 'common/components/UploadInput/UploadInput'
import { capitalizeFirstLetter } from 'common/utils/stringUtils'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { usePopper } from 'common/hooks/usePopper'
import { useEventListener } from 'common/hooks/useEventListener'
import { DeleteConfirmationBanner } from 'common/components/DeleteConfirmationBanner/DeleteConfirmationBanner'
import { UpdateActions } from 'common/components/UpdateActions/UpdateActions'

import { getIsAlwaysActive, getIsCouponActive } from 'modules/ProductInfo/utils/utils'
import AccessDurationInput, { accessDurationOptions, getAccessDurationOption } from './AccessDurationInput'
import PriceInput from '../PricingForm/PriceInput'
import AccessUnitInput from './AccessUnitInput'
import { MIN_PRODUCT_PRICE } from './schema'
import CouponsSuggestions from '../CouponsSuggestions/CouponsSuggestions'
import PromoCoupon from '../PromoCoupon/PromoCoupon'
import { fromDayAnchorToLocalDate } from 'modules/ProductInfo/utils/dateUtils'
import { getIsProductReadyForSale } from 'modules/ProductInfo/hooks/useReadyForSaleTasks'
import { ShareLinkMenu } from 'modules/ProductInfo/components/ShareLinkMenu/ShareLinkMenu'
import { getOneTimeProductPagePath } from 'modules/ProductInfo/utils/shareLinks'

function ProductForm({
  productId,
  product,
  currency,
  coupons,
  coachOrgId,
  onSubmit,
  isUpdating,
  stripeResponsePending,
}) {
  const navigate = useNavigate()
  const { createAlert } = useAlert()

  const [setOneTimeProduct] = useSetOneTimeProductMutation()
  const [setExclusiveTab] = useSetExclusiveTabMutation()
  const [setTabContent] = useSetTabContentMutation()
  const [setStripeRequest] = useSetStripeRequestMutation()

  const [isDeleting, setIsDeleting] = useState(false)
  const [couponSearchValue, setCouponSearchValue] = useState('')
  const [deleteConfirmation, setDeleteConfirmation] = useState(false)

  const methods = useFormContext()
  const {
    watch,
    setValue,
    setError,
    register,
    clearErrors,
    trigger,
    handleSubmit,
    formState: { errors },
  } = methods
  const formState = watch()

  const promosToOrder = (formState?.promo || []).map((p) => ({
    ...p,
    isActive: getIsCouponActive({ promo: p, coupon: coupons?.[p?.stripeCouponId] }),
    expiration: getIsAlwaysActive({ promo: p, coupon: coupons?.[p?.stripeCouponId] })
      ? 0
      : coupons?.[p?.stripeCouponId]?.expiration,
  }))
  const orderedPromos = orderBy(promosToOrder, ['isActive', 'expiration'], ['desc', 'desc']).map((p) => {
    delete p.isActive
    delete p.expiration
    return p
  })

  const { data: publishedCheckoutPage } = useListenPublishedCheckoutPageQuery({ coachOrgId, productId: product?.id })
  const publishedCheckoutPageLoading = publishedCheckoutPage === undefined || publishedCheckoutPage?.isLoading

  // The link points at the storefront, where a product only exists once it is ready for
  // sale — so sharing it is gated on that and nothing else. The published checkout page is
  // read here only for legacy products, which have no readyForSale flag and fall back to
  // the older rule; every other product is answered by its own flag, with no wait.
  const isReadyForSale = getIsProductReadyForSale({
    product,
    publishedCheckoutPage: publishedCheckoutPageLoading ? null : publishedCheckoutPage,
  })

  const { data: coachOrgSlugData } = useListenCoachOrgSlugQuery({ coachOrgId })
  const isCoachOrgSlugLoading = coachOrgSlugData === undefined || coachOrgSlugData?.isLoading
  const coachOrgSlug = isCoachOrgSlugLoading ? '' : coachOrgSlugData || ''

  const { data: productImgData } = useListenProductImgQuery({ coachOrgId, id: productId })
  const { data: itemImgData } = useListenItemPreviewImgQuery({
    coachOrgId,
    itemId: product?.item?.id,
    itemType: product?.item?.type,
  })
  // While these listeners load they return { isLoading: true } instead of a url string,
  // so only use the value once it is an actual url.
  const productImg = typeof productImgData === 'string' ? productImgData : ''
  const itemImg = typeof itemImgData === 'string' ? itemImgData : ''

  let [popperTrigger, popperContainer] = usePopper({
    placement: 'bottom-start',
    strategy: 'fixed',
  })

  const { addInputRef, removeInputRef, moveFocusOnKeyPress } = useFormRefsControl()
  const inputRefsSortMethod = [
    'stripeProductName',
    'description',
    'slug',
    'price',
    'accessDuration',
    'accessEnd',
    'accessUnit',
    'accessValue',
    'coupons',
    'submit',
  ]

  const stripeProductNameInputRef = useRef()
  const descriptionInputRef = useRef()
  const linkInputRef = useRef()
  const priceInputRef = useRef()
  const accessDurationInputRef = useRef()
  const accessEndDateInputRef = useRef()
  const accessUnitInputRef = useRef()
  const accessValueInputRef = useRef()
  const couponInputRef = useRef()
  const submitRef = useRef()

  useEffect(() => {
    addInputRef({ ref: stripeProductNameInputRef, name: 'stripeProductName', sortMethod: inputRefsSortMethod })
    addInputRef({ ref: descriptionInputRef, name: 'description', sortMethod: inputRefsSortMethod })
    addInputRef({ ref: linkInputRef, name: 'slug', sortMethod: inputRefsSortMethod })
    addInputRef({ ref: priceInputRef, name: 'price', sortMethod: inputRefsSortMethod })
    addInputRef({ ref: accessDurationInputRef, name: 'accessDuration', sortMethod: inputRefsSortMethod })
    addInputRef({ ref: accessEndDateInputRef, name: 'accessEnd', sortMethod: inputRefsSortMethod })
    addInputRef({ ref: couponInputRef, name: 'coupons', sortMethod: inputRefsSortMethod })
    addInputRef({ ref: submitRef, name: 'submit', sortMethod: inputRefsSortMethod })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (formState.accessDuration === accessDurationOptions.period) {
      removeInputRef(accessEndDateInputRef, 'accessEnd')
      addInputRef({ ref: accessUnitInputRef, name: 'accessUnit', sortMethod: inputRefsSortMethod })
      addInputRef({ ref: accessValueInputRef, name: 'accessValue', sortMethod: inputRefsSortMethod })
    } else if (formState.accessDuration === accessDurationOptions.date) {
      removeInputRef(accessUnitInputRef, 'accessUnit')
      removeInputRef(accessValueInputRef, 'accessValue')
      addInputRef({
        ref: { current: accessEndDateInputRef?.current?.input },
        name: 'accessEnd',
        sortMethod: inputRefsSortMethod,
      })
      clearErrors('accessValue')
    } else if (formState.accessDuration === accessDurationOptions.forever) {
      removeInputRef(accessEndDateInputRef, 'accessEnd')
      removeInputRef(accessUnitInputRef, 'accessUnit')
      removeInputRef(accessValueInputRef, 'accessValue')
      clearErrors('accessValue')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.accessDuration])

  const handleAddPromo = ({ couponId }) => {
    const coupon = coupons[couponId]

    const promo = {
      stripeCouponId: coupon.id,
      isAutoApplied: false,
    }

    const updatedPromos = orderedPromos ? [...orderedPromos, promo] : [promo]
    setValue('promo', updatedPromos)
  }

  const handleDeletePromo = ({ stripeCouponId }) => {
    const updatedPromos = (orderedPromos || []).filter((pr) => pr.stripeCouponId !== stripeCouponId)
    setValue('promo', updatedPromos)
  }

  const handleUpdateAutoApplied = ({ isAutoApplied, stripeCouponId }) => {
    const updatedPromos = orderedPromos.map((p) =>
      p?.stripeCouponId === stripeCouponId
        ? { ...p, isAutoApplied }
        : {
            ...p,
            isAutoApplied: Boolean(p?.isAutoApplied) && isAutoApplied ? false : Boolean(p?.isAutoApplied),
          }
    )
    setValue('promo', updatedPromos)
  }

  const handleDeleteProduct = async () => {
    setIsDeleting(true)
    await setStripeRequest({
      requestId: product?.id,
      coachOrgId,
      type: 'DELETE_ONE_TIME_PRODUCT',
      payload: {
        trybeProductId: product?.id,
        stripeProductId: product?.stripeProductId,
      },
    })
    await setOneTimeProduct({
      coachOrgId,
      product: null,
      productId: product?.id,
    })

    if (product?.item?.type === 'tab' && Boolean(product?.item?.id)) {
      setExclusiveTab({ coachOrgId, tabId: product.item.id, tab: null })
      setTabContent({ coachOrgId, tabId: product.item.id, tabContent: null })
    }
    setIsDeleting(true)
    createAlert({ text: 'Product deleted!', type: 'success' })
    navigate(SELL)
  }

  useEventListener('keydown', (e) => moveFocusOnKeyPress(e, handleSubmit(onSubmit)))

  return (
    <form
      id='productForm'
      onSubmit={(e) => {
        if (e.nativeEvent?.submitter?.getAttribute?.('form') !== 'productForm') {
          e.preventDefault()
          return
        }
        handleSubmit(onSubmit)(e)
      }}
      onKeyDown={(e) => {
        if (e.code === 'Enter') {
          e.preventDefault() //Otherwise form autosubmits on each enter press
        }
      }}
    >
      <div className='mt-12 max-w-2xl bg-white rounded-xl overflow-hidden border-2 border-gray-200'>
        <div className='px-8 pb-6 pt-8 border-b border-gray-200'>
          <div>
            <Label htmlFor='stripeProductName' tw='mb-1'>
              Name
            </Label>
            <Input
              inputRef={stripeProductNameInputRef}
              name='stripeProductName'
              register={register}
              placeholder='Enter name'
              error={Boolean(errors?.stripeProductName?.message)}
              autoFocus={true}
            />
            {errors && errors.stripeProductName && <InputError>{errors.stripeProductName.message}</InputError>}
          </div>
          <div className='pt-6'>
            <Label htmlFor='description' tw='mb-1'>
              Description
            </Label>
            <Input
              inputRef={descriptionInputRef}
              name='description'
              register={register}
              placeholder='Enter description'
              error={Boolean(errors?.description?.message)}
            />
            {errors && errors.description && <InputError>{errors.description.message}</InputError>}
          </div>
          <div className='pt-6 flex flex-col items-start'>
            <Label htmlFor='slug' tw='mb-1'>
              Link
            </Label>
            <Input
              inputRef={linkInputRef}
              name='slug'
              register={register}
              registerOptions={{
                onChange: (e) => {
                  const updatedValue = e.target.value.replace(/[\W_]+/g, '-').toLowerCase()
                  setValue('slug', updatedValue, { shouldValidate: true })
                },
              }}
              placeholder='Enter link'
              error={Boolean(errors?.slug?.message)}
            />
            {errors && errors.slug && <InputError>{errors.slug.message}</InputError>}
            <ShareLinkMenu
              coachSlug={coachOrgSlug}
              pagePath={getOneTimeProductPagePath({ slug: formState?.slug, salesPage: product?.salesPage })}
              isReadyForSale={isReadyForSale}
              className='mt-2 bg-gray-100 rounded py-1 px-1.5 text-xs flex items-center max-w-[100%] cursor-pointer'
              css={[isCoachOrgSlugLoading && tw`invisible`]}
            >
              <span className='truncate'>
                https://my.trybe.fit/{coachOrgSlug}/product/
                <span className='font-bold truncate'>{formState?.slug || 'your-slug'}</span>
              </span>
            </ShareLinkMenu>
          </div>
        </div>
        <div className='px-8 py-6 border-b border-gray-200'>
          <Label htmlFor='price' tw='mb-1'>
            Price
          </Label>
          <PriceInput
            inputRef={priceInputRef}
            name='price'
            placeholder='0.00'
            value={formState.price}
            onChange={(price) => {
              if (errors?.price?.message && price >= MIN_PRODUCT_PRICE) {
                clearErrors('price')
              } else if (price < MIN_PRODUCT_PRICE) {
                trigger('price')
              }
              setValue('price', Number(price))
            }}
            error={Boolean(errors?.price?.message)}
            currency={currency}
          />
          {errors && errors.price && <InputError>{errors.price.message}</InputError>}
        </div>
        <div className='px-8 py-6 border-b border-gray-200'>
          <Label htmlFor='accessDuration' tw='mb-1'>
            Access Duration
          </Label>
          <AccessDurationInput
            inputRef={accessDurationInputRef}
            value={formState?.accessDuration}
            setValue={setValue}
          />
          {formState.accessDuration === accessDurationOptions.date && (
            <div className='pt-6'>
              <Label htmlFor='accessDuration' tw='mb-1'>
                Access End
              </Label>
              <DatePicker
                name='accessEnd'
                ref={accessEndDateInputRef}
                selected={formState.accessEnd}
                onChange={(date) => {
                  if (date) {
                    clearErrors('accessEnd')
                    setValue('accessEnd', date)
                  }
                }}
                maxDate={formState.expiration}
                placeholderText='mm/dd/yyyy'
                disabled={formState.alwaysActive}
                className='text-ellipsis bg-inherit cursor-pointer'
                css={[inputBase, errors.accessEnd && tw`ring-red-500`, tw`!pl-10`]}
                showIcon={true}
                icon={
                  <CgCalendar
                    className='!w-6 !h-6 top-1/2 -translate-y-1/2'
                    onClick={() => accessEndDateInputRef?.current?.setOpen(true)}
                  />
                }
                popperProps={{
                  placement: 'bottom-start',
                  strategy: 'fixed',
                }}
                onKeyDown={(e) => {
                  if (e.code === 'Tab') {
                    e.preventDefault()
                    e.stopPropagation()
                    accessEndDateInputRef?.current?.setOpen(false)
                    moveFocusOnKeyPress(e)
                  }
                }}
              />
            </div>
          )}
          {formState.accessDuration === accessDurationOptions.period && (
            <>
              <div className='pt-6'>
                <Label htmlFor='accessUnit' tw='mb-1'>
                  Access Unit
                </Label>
                <AccessUnitInput
                  inputRef={accessUnitInputRef}
                  value={formState?.accessUnit}
                  setValue={setValue}
                  accessValueErr={Boolean(errors?.accessValue?.message)}
                  setError={setError}
                />
              </div>
              <div className='pt-6'>
                <Label htmlFor='accessValue' tw='mb-1'>
                  Number of {capitalizeFirstLetter(formState.accessUnit)}s
                </Label>
                <Input
                  inputRef={accessValueInputRef}
                  name='accessValue'
                  placeholder={`Enter access duration in ${formState.accessUnit}s`}
                  error={Boolean(errors?.accessValue?.message)}
                  register={register}
                  registerOptions={{
                    onChange: (e) => {
                      const value = parseInt(e.target.value) || ''
                      if (value >= 1) {
                        clearErrors('accessValue')
                      }
                      setValue('accessValue', value)
                    },
                  }}
                  type='number'
                  min={1}
                />
                {Boolean(errors?.accessValue?.message) && <InputError>{errors.accessValue.message}</InputError>}
              </div>
            </>
          )}
        </div>
        <div className='px-8 py-6 border-b border-gray-200'>
          <UploadInput
            name='previewImg'
            label='Thumbnail Image'
            labelDescription='16:9 landscape image'
            register={register}
            setValue={setValue}
            coachOrgId={coachOrgId}
            id={productId}
            uploadType='one-time-product-thumbnail'
            liveUrl={productImg || product?.previewImg || itemImg}
            setError={setError}
            previewIcon={<CgImage className='!w-5 !h-5' />}
            clearErrors={clearErrors}
            fileType='image'
          />
          {errors?.previewImg ? <InputError>{errors.previewImg.message}</InputError> : null}
        </div>
        <div className='px-8 py-6'>
          <Label htmlFor='headlessui-combobox-input-1' tw='mb-1'>
            Eligible Coupons
          </Label>
          <div className='relative mb-2'>
            <Combobox
              value={null}
              onChange={(couponId) => {
                if (couponId) {
                  handleAddPromo({ couponId })
                }
              }}
            >
              <div ref={popperTrigger} className='w-full'>
                <Combobox.Input
                  name='coupons'
                  ref={(e) => {
                    couponInputRef.current = e
                  }}
                  placeholder={orderedPromos?.length > 10 ? '10 coupon limit reached' : 'Enable a coupon'}
                  autoComplete='off'
                  displayValue={(couponId) => (couponId ? coupons[couponId].name || '' : '')}
                  css={[inputBase, tw`px-4`]}
                  onChange={(e) => setCouponSearchValue(e.target.value)}
                  disabled={orderedPromos?.length > 10}
                />
              </div>
              {couponSearchValue.length > 0 && (
                <CouponsSuggestions
                  coupons={coupons}
                  searchValue={couponSearchValue}
                  product={product}
                  inputRef={couponInputRef}
                  popperContainer={popperContainer}
                  currency={currency}
                  productType='oneTimeProduct'
                />
              )}
            </Combobox>
          </div>
          {orderedPromos.map((promo) => {
            const coupon = Object.values(coupons).find((coupon) => coupon.id === promo.stripeCouponId)
            return (
              <PromoCoupon
                key={promo?.stripeCouponId}
                promo={promo}
                coupon={coupon}
                handleDeletePromo={handleDeletePromo}
                handleUpdateAutoApplied={handleUpdateAutoApplied}
              />
            )
          })}
        </div>
        {deleteConfirmation ? (
          <DeleteConfirmationBanner
            text='Are you sure?'
            handleDelete={handleDeleteProduct}
            handleGoBack={() => setDeleteConfirmation(false)}
            loading={isDeleting}
          />
        ) : (
          <UpdateActions
            itemKey={productId}
            loading={isUpdating || isDeleting || stripeResponsePending || stripeResponsePending}
            handleDelete={() => setDeleteConfirmation(true)}
            containerClasses='md:!px-8'
            // deleteDisabled={isUploadingAssets(assetDrafts)}
            // disabledReason='Please wait for media upload to complete'
            actionText='Save'
            form='productForm'
            ref={submitRef}
          />
        )}
      </div>
    </form>
  )
}

export default ProductForm

export const getDefaultProductValues = (product, fallbackPreviewImg = '') => ({
  id: product?.id,
  stripeProductName: product?.stripeProductName || '',
  description: product?.description || '',
  slug: product?.slug || '',
  price: product?.price || 0,
  accessDuration: getAccessDurationOption({
    accessEnd: product?.accessEnd,
    accessUnit: product?.accessUnit,
  }),
  accessEnd: product?.accessEnd ? fromDayAnchorToLocalDate(product.accessEnd) : null,
  accessUnit: product?.accessUnit || null,
  accessValue: product?.accessValue || null,
  previewImg: product?.previewImg || fallbackPreviewImg || '',
  promo: product?.promo || [],
})

const Label = tw.label`flex cursor-pointer font-semibold text-tBlack`
const InputError = tw.p`flex text-xs mt-1 text-tRed`
